import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type SVGProps,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@immo/ui-kit';
import { MoreVerticalIcon } from '../icons';

// Anchored to the trigger's right edge always; vertically it anchors below
// (`top`) by default and flips above (`bottom`) when the menu would overflow the
// viewport bottom. Its height is clamped to the room on the chosen side so it
// scrolls inside itself instead of running off-screen (BL-004). Mirrors the
// upward-anchor idiom in `layout/user-menu.tsx`.
type MenuPosition = { readonly right: number; readonly maxHeight: number } & (
  { readonly top: number } | { readonly bottom: number }
);

// Density caps in rem. The `max-h-80` / `max-h-[40rem]` classes on the popup repeat
// them for the first, hidden, measuring render; keep the two in step.
const MENU_CAP_REM = { default: 20, compact: 40 } as const;
// Space between trigger and menu, and the least the menu keeps from the viewport edge it opens towards.
const GAP = 4;
const VIEWPORT_MARGIN = 8;

function positionStyle(position: MenuPosition | null): CSSProperties {
  if (position === null) return { visibility: 'hidden' };

  return 'top' in position
    ? { top: position.top, right: position.right, maxHeight: position.maxHeight }
    : { bottom: position.bottom, right: position.right, maxHeight: position.maxHeight };
}

function menuItems(menu: HTMLElement | null): HTMLElement[] {
  return menu === null ? [] : Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)'));
}

export type ActionsMenuItem = {
  readonly key: string;
  readonly label: string;
  readonly icon?: ComponentType<SVGProps<SVGSVGElement>>;
  readonly tone?: 'default' | 'danger';
  readonly disabled?: boolean;
  readonly reason?: string;
  /**
   * Why this item cannot be activated right now, reachable by hover AND
   * keyboard focus (BL-061). When present it wins over `disabled`/`reason`: the
   * item renders `aria-disabled` instead of the native `disabled` attribute
   * (which would take it out of the tab order and make the reason unreachable),
   * no `title` is emitted, and `onSelect` is never invoked.
   */
  readonly disabledReason?: string;
  readonly onSelect: () => void;
};

export type ActionsMenuSeparator = { readonly key: string; readonly separator: true };
/** A non-interactive group label; roving focus skips it because it is not a `menuitem`. */
export type ActionsMenuHeading = { readonly key: string; readonly heading: string };
export type ActionsMenuEntry = ActionsMenuItem | ActionsMenuSeparator | ActionsMenuHeading;

type Props = {
  readonly items: ReadonlyArray<ActionsMenuEntry>;
  readonly triggerLabel: string;
  /** Overrides the 36 px kebab box. A table header row is shorter than a toolbar. */
  readonly triggerClassName?: string;
  /** Replaces the kebab icon with a labelled trigger; `triggerClassName` then styles it alone. */
  readonly triggerContent?: ReactNode;
  /** `compact` fits a long closed list (the placeholder menu) without scrolling. */
  readonly density?: 'default' | 'compact';
  /** Called when the trigger opens the menu, never on close — a caller that loads its rows lazily starts here. */
  readonly onOpen?: () => void;
  /** Shown in the popup in place of the rows while `items` is empty (loading, empty and failed states). */
  readonly emptyContent?: ReactNode;
  /** Merged onto the popup, e.g. a fixed width. */
  readonly menuClassName?: string;
};

export function ActionsMenu({
  items,
  triggerLabel,
  triggerClassName,
  triggerContent,
  density = 'default',
  onOpen,
  emptyContent,
  menuClassName,
}: Props) {
  const compact = density === 'compact';
  const rowSizing = compact ? 'px-3 py-[3px] text-base-sm' : 'px-3 py-2 text-base';
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const reasonBaseId = useId();

  // Hand the page back to the control the member came from. Called on the two
  // closes the member drives from the keyboard — choosing an item, and Escape —
  // and deliberately NOT on the outside press or the scroll close: the press has
  // its own new focus target, and restoring focus from a `mousedown` handler
  // without `preventDefault()` is undone by the browser's default action right
  // after React flushes (BL-031).
  const closeAndReturnFocus = useCallback(() => {
    triggerRef.current?.focus();
    setOpen(false);
  }, []);

  // Measure the trigger and the rendered menu to place the popup, flipping it
  // upward when it would overflow below and more room exists above, then clamping
  // its height to the room on the chosen side. Re-measures
  // on resize — the measurement half mirrors the `reposition()` idiom in
  // `document-category-picker.tsx` — and closes on scroll, which those siblings
  // deliberately do not do.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);

      return;
    }

    const reposition = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;

      if (trigger === null || menu === null) return;

      const rect = trigger.getBoundingClientRect();
      const rootFontPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const capPx = MENU_CAP_REM[density] * rootFontPx;
      // `scrollHeight` ignores the `max-height` a previous pass set, so rows that
      // arrive after opening are judged on their natural height, not the clamp.
      const natural = Math.max(menu.getBoundingClientRect().height, menu.scrollHeight);
      const wanted = Math.min(natural, capPx);
      const right = window.innerWidth - rect.right;
      const spaceBelow = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;
      const flip = wanted > spaceBelow && spaceAbove > spaceBelow;
      const maxHeight = Math.min(capPx, flip ? spaceAbove : spaceBelow);

      setPosition(
        flip
          ? { bottom: window.innerHeight - rect.top + GAP, right, maxHeight }
          : { top: rect.bottom + GAP, right, maxHeight },
      );
    };

    // The menu is itself a scroll container (`max-h-80 overflow-y-auto`), and a
    // capture-phase listener on `window` sees scrolls that originate inside it,
    // so scrolling a long menu would otherwise close it mid-read.
    const close = (event: Event) => {
      const target = event.target as Node | null;

      if (target !== null && menuRef.current !== null && menuRef.current.contains(target)) return;

      setOpen(false);
    };

    reposition();
    window.addEventListener('resize', reposition);
    // A menu anchored to a row cannot follow that row out of the viewport
    // without drifting over the topbar and off-screen, so scrolling closes it;
    // `resize` still re-measures because the trigger stays put there. Capture
    // phase catches scrolls in any ancestor container, not just the window.
    window.addEventListener('scroll', close, true);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', close, true);
    };
    // Rows that arrive after opening change the popup's height, so it is re-measured and re-flipped.
  }, [open, items.length, density]);

  // Roving focus (WAI-ARIA menu): opening lands on the first item, the arrow keys,
  // Home and End move between the enabled ones. Without it the menu is reachable
  // by keyboard only as a run of Tab stops in a portal at the end of the document.
  useEffect(() => {
    // The popup stays `visibility: hidden` until it is placed, and a hidden element
    // cannot take focus, so the first focus waits for the position.
    if (open && position !== null && !menuRef.current?.contains(document.activeElement)) {
      menuItems(menuRef.current)[0]?.focus();
    }
  }, [open, position, items.length]);

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const entries = menuItems(menuRef.current);
    const at = entries.indexOf(document.activeElement as HTMLElement);
    const target = { ArrowDown: at + 1, ArrowUp: at - 1, Home: 0, End: entries.length - 1 }[e.key];

    if (target === undefined || entries.length === 0) return;

    e.preventDefault();
    entries[(target + entries.length) % entries.length]?.focus();
  };

  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current !== null && triggerRef.current.contains(target);
      const inMenu = menuRef.current !== null && menuRef.current.contains(target);

      if (!inTrigger && !inMenu) setOpen(false);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAndReturnFocus();
    };

    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, closeAndReturnFocus]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!open) onOpen?.();

          setOpen((v) => !v);
        }}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          triggerContent === undefined
            ? cn(
                'inline-flex h-9 w-9 items-center justify-center rounded-md text-text-tertiary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary focus-visible:outline-none focus-visible:shadow-focus',
                triggerClassName,
              )
            : triggerClassName
        }
      >
        {triggerContent ?? <MoreVerticalIcon className="h-4 w-4" aria-hidden />}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            onKeyDown={onMenuKeyDown}
            // Rendered before it is measured so `reposition()` can read its
            // height; kept invisible (not unmounted) until placed to avoid a
            // one-frame flash at the wrong spot.
            style={positionStyle(position)}
            className={cn(
              'fixed z-50 min-w-[200px] overflow-y-auto rounded-lg border border-border bg-surface py-1 shadow-md',
              compact ? 'max-h-[40rem]' : 'max-h-80',
              menuClassName,
            )}
          >
            {items.length === 0 && emptyContent}
            {items.map((item) => {
              if ('separator' in item) return <div key={item.key} role="separator" className="my-1 h-px bg-border" />;

              if ('heading' in item) {
                return (
                  <div
                    key={item.key}
                    role="presentation"
                    className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary"
                  >
                    {item.heading}
                  </div>
                );
              }

              const Icon = item.icon;

              if (item.disabledReason !== undefined) {
                const reasonId = `${reasonBaseId}-${item.key}-reason`;

                return (
                  <div key={item.key} className="group relative">
                    <button
                      type="button"
                      role="menuitem"
                      aria-disabled="true"
                      aria-describedby={reasonId}
                      // No `onClick`, so `item.onSelect` is never wired for this
                      // branch — that omission is the whole guard against
                      // activation. No `pointer-events-none` either: that would
                      // recreate the BL-061 failure this shape exists to avoid.
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md text-left transition-colors duration-micro ease-out',
                        rowSizing,
                        item.tone === 'danger' ? 'text-danger-600' : 'text-text-primary',
                        'cursor-not-allowed opacity-50 hover:bg-transparent',
                        'focus-visible:outline-none focus-visible:shadow-focus-row',
                      )}
                    >
                      {Icon !== undefined && <Icon className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />}
                      <span className="flex-1 truncate">{item.label}</span>
                    </button>
                    <span
                      id={reasonId}
                      role="tooltip"
                      className="pointer-events-none absolute right-0 top-full z-50 mt-2 w-max max-w-xs whitespace-normal rounded-md bg-tooltip px-2 py-1 text-sm text-neutral-0 opacity-0 shadow-md transition-opacity duration-micro ease-out group-hover:opacity-100 group-focus-within:opacity-100"
                    >
                      {item.disabledReason}
                    </span>
                  </div>
                );
              }

              return (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled === true}
                  title={item.reason}
                  onClick={() => {
                    // Focus FIRST, synchronously, before `onSelect` runs. `onSelect`
                    // typically opens a Modal, and `Modal`'s open-effect captures
                    // `document.activeElement` to restore on close. Unmounting this
                    // portalled item first drops focus to <body>, so the dialog
                    // captures a node that cannot take focus back and the member is
                    // returned to the top of the document (IMM-905 · UX-6).
                    closeAndReturnFocus();
                    item.onSelect();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md text-left transition-colors duration-micro ease-out',
                    rowSizing,
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    'focus-visible:outline-none focus-visible:shadow-focus-row',
                    item.tone === 'danger'
                      ? 'text-danger-600 hover:bg-danger-50 focus-visible:bg-danger-50'
                      : 'text-text-primary hover:bg-hover-bg focus-visible:bg-hover-bg',
                  )}
                >
                  {Icon !== undefined && <Icon className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />}
                  <span className="flex-1 truncate">{item.label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
